import React, { useEffect, useState } from 'react'
import { css } from '@emotion/css'
import { formatDistanceToNow } from 'date-fns'
import { Button } from 'design-system/ui/atoms/button'
import { DataTable } from 'design-system/ui/molecules/table'
import { Trash2Icon } from 'lucide-react'

import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { WizardBox } from '~/ui/screens/appScreen/components/WizardBox'
import { prettifyString } from '~/utils/strapi'
import { useDeleteMcpServer, useMcpServers, useNewServerWizard } from './hooks'
import { NewServerWizard } from './NewServerWizard'

// Define the type for MCP server data
export interface McpServer {
  id: string
  name: string
  url: string
  createdAt: string
  updatedAt: string
  connectedId?: string
  entityId?: string
  apps?: string[]
}

export const gridBox = css`
  display: grid;
  grid-template-columns: 200px 300px 150px 150px 180px;
  gap: 10px;
  width: 100%;
`

interface McpServerScreenProps {
  showNewWizard?: boolean
  appName: string
  appLogo?: string
  setShowNewWizard?: (b: boolean) => void
  connectedAccountId?: string
  entityId?: string
  integrationId?: string
  connectorInfo?: {
    id: string
    uuid: string
  }
}

export default function McpServerScreen({
  showNewWizard = false,
  setShowNewWizard = (b: boolean) => {},
  appName,
  appLogo,
  connectedAccountId,
  entityId,
  integrationId = '',
  connectorInfo,
}: McpServerScreenProps) {
  const {
    data: mcpServers = [],
    isLoading,
    isError,
  } = useMcpServers({
    authConfigIds: integrationId ? integrationId : '',
    limit: 9999
  })

  const [selectedServerId, setSelectedServerId] = useState<string | undefined>()
  const deleteMutation = useDeleteMcpServer()

  // Use our custom wizard hook
  const {
    resetState,
    currentStep,
    setCurrentStep,
    isWizardOpen,
    setIsWizardOpen,
  } = useNewServerWizard({
    appName,
    initialServerId: selectedServerId,
    serverURL: mcpServers.find((server) => server.id === selectedServerId)
      ?.mcp_url,
    connectedAccountId,
    initialEntityId: entityId,
  })

  useEffect(() => {
    setIsWizardOpen(showNewWizard)
  }, [showNewWizard])

  useEffect(() => {
    if (!isWizardOpen) {
      setCurrentStep(1)
    }
  }, [isWizardOpen])

  const handleDeleteServer = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete the server "${name}"?`)) {
      deleteMutation.mutate({ id })
    }
  }
  // Define columns for the DataTable
  const columns = React.useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }: { row: { original: { name: string } } }) => (
          <div className="flex items-center gap-3">
            <div className="text-[12px] font-semibold">{row.original.name}</div>
          </div>
        ),
        size: 200,
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }: { row: { original: { mcp_url: string } } }) => (
          <div className="text-[12px] font-[400] text-black-600">
            {row.original.mcp_url.replace(/\?transport=sse$/, '') + '/mcp'}
          </div>
        ),
        size: 700,
      },
      {
        accessorKey: 'updated_at',
        header: 'Last Updated',
        cell: ({ row }: { row: { original: { updated_at: string } } }) => (
          <div className="text-[12px] font-[400] text-black-600">
            {formatDistanceToNow(new Date(row.original.updated_at), {
              addSuffix: true,
            })}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 250,
        cell: ({
          row,
        }: {
          row: { original: { id: string; name: string } }
        }) => (
          <div className="flex gap-2 pr-3">
            <Button
              size="28"
              variant="primary"
              onClick={(e) => {
                e.stopPropagation()
                // Open the wizard with the server setup
                setSelectedServerId(row.original.id)
                setCurrentStep(2)
                setIsWizardOpen(true)
              }}
            >
              Open
            </Button>
            <Button
              variant="secondary"
              size="28"
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteServer(row.original.id, row.original.name)
              }}
            >
              <span className="flex items-center gap-2">
                Delete
                <Trash2Icon size={16} />
              </span>
            </Button>
          </div>
        ),
      },
    ],
    [setIsWizardOpen, setCurrentStep, setSelectedServerId, handleDeleteServer],
  )

  // Empty state component
  const emptyState = (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="text-[17px] font-[500] text-black-200">
        No MCP servers found
      </div>
      <div className="text-[14.5px] font-[400] text-black-600">
        Create a new MCP server to manage your deployments.
      </div>
    </div>
  )

  // Error component
  const errorComponent = (
    <div className="mt-10 text-center text-red-500">
      Failed to fetch MCP servers. Please try again.
    </div>
  )

  return (
    <div className="h-full text-black-400">
      <WizardBox
        open={isWizardOpen}
        currentStep={currentStep}
        title={`Setup ${prettifyString(appName)} MCP`}
        logo={appLogo}
        steps={[
          {
            title: 'Server Configuration',
          },
          {
            title: 'Start MCP Client',
          },
        ]}
        setOpen={(b) => {
          setIsWizardOpen(b)
          if (!b) {
            resetState()
          }
          setShowNewWizard?.(b)
        }}
      >
        <NewServerWizard
          appName={appName}
          serverId={selectedServerId}
          connectedAccountId={connectedAccountId}
          entityId={entityId}
          connectorInfoId={connectorInfo?.id}
        />
      </WizardBox>

      <ErrorBoundary
        fallback={
          <div className="">{`Something went wrong. We'll work on getting this fixed.`}</div>
        }
      >
        <div className="mt-[18px]">
          <DataTable
            // @ts-expect-error Not handling data table errors for now
            columns={columns}
            data={mcpServers as unknown as McpServer[]}
            isLoading={isLoading}
            isError={isError}
            emptyState={emptyState}
            errorComponent={errorComponent}
            onRowClick={(row: McpServer) => {
              setSelectedServerId(row.id)
              setCurrentStep(2)
              setIsWizardOpen(true)
            }}
          />
        </div>
      </ErrorBoundary>
    </div>
  )
}
